import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const aboutStats = [
  { value: '150+', key: 'projects' },
  { value: '$50M+', key: 'revenue' },
  { value: '95%', key: 'retention' },
  { value: '40%', key: 'efficiency' },
];

const About = () => {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-black"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Subtle grid background */}
      <div className="absolute inset-0 grid-pattern opacity-30" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-[7.6923%]">
        <div className={`grid lg:grid-cols-2 gap-16 lg:gap-24 items-center ${isRTL ? 'lg:grid-flow-dense' : ''}`}>
          {/* Left Column - Content */}
          <div className={isRTL ? 'lg:col-start-2' : ''}>
            {/* Section Label */}
            <div
              className={`inline-flex items-center gap-3 mb-6 opacity-0 ${
                isVisible ? 'animate-slide-in-left' : ''
              }`}
            >
              <span className="w-12 h-[2px] bg-[#00FFD1]" />
              <span className="text-[#00FFD1] text-sm font-medium tracking-wider uppercase">
                {t('about.title')}
              </span>
            </div>

            {/* Experience Badge */}
            <div
              className={`flex items-baseline gap-2 mb-8 opacity-0 ${
                isVisible ? 'animate-slide-in-left delay-100' : ''
              }`}
            >
              <span className="text-7xl md:text-8xl font-bold text-[#00FFD1]">
                {t('about.experience')}
              </span>
              <span className="text-lg text-white/60">
                {t('about.experienceLabel')}
              </span>
            </div>

            {/* Description Paragraphs */}
            <p
              className={`body-medium text-white/70 mb-6 leading-relaxed opacity-0 ${
                isVisible ? 'animate-slide-in-left delay-200' : ''
              }`}
            >
              {t('about.description1')}
            </p>
            <p
              className={`body-medium text-white/70 mb-6 leading-relaxed opacity-0 ${
                isVisible ? 'animate-slide-in-left delay-300' : ''
              }`}
            >
              {t('about.description2')}
            </p>
            <p
              className={`body-medium text-white/70 mb-6 leading-relaxed opacity-0 ${
                isVisible ? 'animate-slide-in-left delay-400' : ''
              }`}
            >
              {t('about.description3')}
            </p>

            {/* CTA Link */}
            <a
              href="#services"
              className={`inline-flex items-center gap-2 text-[#00FFD1] hover:text-white transition-colors duration-300 mt-4 opacity-0 ${
                isVisible ? 'animate-slide-in-left delay-500' : ''
              }`}
            >
              <span>{t('about.exploreServices')}</span>
              <span className={`text-lg ${isRTL ? 'rotate-180' : ''}`}>→</span>
            </a>
          </div>

          {/* Right Column - Stats Grid */}
          <div
            className={`grid grid-cols-2 gap-6 opacity-0 ${
              isVisible ? 'animate-slide-in-right delay-300' : ''
            } ${isRTL ? 'lg:col-start-1' : ''}`}
          >
            {aboutStats.map((stat, index) => (
              <div
                key={stat.key}
                className="group relative p-8 bg-white/[0.03] border border-white/10 hover:border-[#00FFD1]/30 transition-all duration-500 hover-glow"
              >
                {/* Corner Accent */}
                <div className={`absolute top-0 ${isRTL ? 'right-0 border-t-2 border-r-2' : 'left-0 border-t-2 border-l-2'} w-4 h-4 border-[#00FFD1] opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className={`absolute bottom-0 ${isRTL ? 'left-0 border-b-2 border-l-2' : 'right-0 border-b-2 border-r-2'} w-4 h-4 border-[#00FFD1] opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                
                <div className="text-4xl md:text-5xl font-bold text-white mb-2 group-hover:text-[#00FFD1] transition-colors duration-500">
                  {stat.value}
                </div>
                <div className="text-sm text-white/50 uppercase tracking-wider">
                  {t(`about.stats.${stat.key}`)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
